/**
 * What the SPA does when the refusal gate answers 403 `surface_not_available`
 * (SPEC-TENANT-SURFACES RULE-015, RULE-006).
 *
 * The cached answer of `me/surfaces` can list a surface the gate already
 * refuses - an admin hid it inside the 30 s bound. When that happens the SPA
 * MUST (1) invalidate that ONE cache key and refetch, and (2) render the SAME
 * not-found state an unregistered route renders (SPEC-FRONTEND RULE-094
 * clause 4) - never an error dialog, and never a retry loop against the refusal.
 *
 * HOW. {@link installSurfaceRefusalHandler} registers an observer on the shared
 * API client (`subscribeResponseError`, api/client.ts) and:
 * - invalidates `["me", "surfaces"]` ONCE per burst: while an invalidation is in
 *   flight, and for {@link SURFACES_STALE_TIME_MS} after it, further refusals do
 *   not invalidate again - so a page whose calls keep being refused cannot drive
 *   a refetch loop against `me/surfaces`;
 * - stops TanStack from RETRYING a query the gate refused (a refusal is not
 *   transient; repeating it is the retry loop RULE-015 forbids), by wrapping
 *   the client's default `retry` for the lifetime of the handler;
 * - publishes a {@link SurfaceRefusal} signal the router reads through
 *   {@link useSurfaceRefusal} to render its not-found page. Rendering that page
 *   is the router's job (TASK-TSURF-W3-NAV-ROUTE-FILTER), not this module's.
 *
 * @module surfaces/surfaceRefusal
 * @see SPEC-TENANT-SURFACES-RULE-015
 * @see TASK-TSURF-W3-SURFACES-STORE-FE-CHG-005
 */
import { useSyncExternalStore } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { subscribeResponseError } from "../api/client";
import { SURFACES_STALE_TIME_MS, surfacesQueryKey } from "./surfacesQuery";

/** The typed code of the refusal gate's 403 (internal/api/middleware/surface_gate.go). */
export const SURFACE_NOT_AVAILABLE_CODE = "surface_not_available";

/** Is this error the refusal gate's 403 `surface_not_available`? */
export function isSurfaceRefusal(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  if (error.response?.status !== 403) return false;
  const body = error.response.data as { error?: unknown } | undefined;
  return body?.error === SURFACE_NOT_AVAILABLE_CODE;
}

// ---------------------------------------------------------------------------
// The not-found signal
// ---------------------------------------------------------------------------

/** The latest refusal: which call was refused, and when. */
export interface SurfaceRefusal {
  method: string;
  url: string;
  at: number;
}

let _latest: SurfaceRefusal | null = null;
const _listeners = new Set<() => void>();

function publish(next: SurfaceRefusal | null): void {
  _latest = next;
  for (const listener of Array.from(_listeners)) listener();
}

function subscribe(listener: () => void): () => void {
  _listeners.add(listener);
  return () => {
    _listeners.delete(listener);
  };
}

function getSnapshot(): SurfaceRefusal | null {
  return _latest;
}

/** The latest unhandled refusal, or null. The router renders its not-found page while it is set. */
export function useSurfaceRefusal(): SurfaceRefusal | null {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Clear the signal (the router calls it when the location changes). */
export function clearSurfaceRefusal(): void {
  if (_latest !== null) publish(null);
}

// ---------------------------------------------------------------------------
// Installation
// ---------------------------------------------------------------------------

type RetryOption = boolean | number | ((failureCount: number, error: unknown) => boolean) | undefined;

function withoutRefusalRetry(base: RetryOption): (failureCount: number, error: unknown) => boolean {
  return (failureCount, error) => {
    if (isSurfaceRefusal(error)) return false;
    if (typeof base === "function") return base(failureCount, error);
    if (typeof base === "number") return failureCount < base;
    if (typeof base === "boolean") return base;
    return failureCount < 3; // TanStack's own default when none is configured
  };
}

/**
 * Register the refusal handler for one QueryClient. Returns the uninstall
 * function, which also restores the client's previous default `retry`.
 */
export function installSurfaceRefusalHandler(queryClient: QueryClient): () => void {
  let inFlight = false;
  let lastInvalidationAt = Number.NEGATIVE_INFINITY;

  const previousDefaults = queryClient.getDefaultOptions();
  const previousRetry = previousDefaults.queries?.retry as RetryOption;
  queryClient.setDefaultOptions({
    ...previousDefaults,
    queries: { ...previousDefaults.queries, retry: withoutRefusalRetry(previousRetry) },
  });

  const unsubscribe = subscribeResponseError((error) => {
    if (!isSurfaceRefusal(error)) return;

    publish({
      method: (error.config?.method ?? "get").toUpperCase(),
      url: error.config?.url ?? "",
      at: Date.now(),
    });

    const now = Date.now();
    if (inFlight || now - lastInvalidationAt < SURFACES_STALE_TIME_MS) return;
    inFlight = true;
    lastInvalidationAt = now;
    void queryClient
      .invalidateQueries({ queryKey: surfacesQueryKey(), exact: true })
      .finally(() => {
        inFlight = false;
      });
  });

  return () => {
    unsubscribe();
    const current = queryClient.getDefaultOptions();
    queryClient.setDefaultOptions({
      ...current,
      queries: { ...current.queries, retry: previousRetry },
    });
  };
}
